import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { RadioButton } from 'primereact/radiobutton';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Slider } from 'primereact/slider';
import { useProfile } from '../context/ProfileContext';
import { useAuth } from '../auth/useAuth';
import { swingApi } from '../api/swingApi';
import { promotionApi } from '../api/promotionApi';
import { SaveProfileInput } from '../api/profileApi';
import { CaddiePrimaryFocus, CaddieRecommendationDepth, CaddieTalkativeness, CaddieVoicePreference, CaddieVoiceStyle, ClubSwingAverage, MeasurementSystem, UpsertClubInput } from '../types';
import { US_STATES } from '../constants/usStates';
import { DEFAULT_BAG, GOLF_CLUBS } from '../constants/golfClubs';
import { distanceUnitLabel, heightUnitLabel, speedUnitLabel, toCanonicalDistanceYards, toCanonicalHeightFeet, toCanonicalSpeedMph, toDisplayDistance, toDisplayHeight, toDisplaySpeed } from '../constants/units';
import CourseSearchPanel from '../components/CourseSearchPanel';
import CaddieAdjustments from '../components/CaddieAdjustments';
import ProfileMembership from '../components/ProfileMembership';

// Avatar images are cropped to a square and scaled down before being
// stored as a data URL, keeping localStorage small.
const AVATAR_SIZE = 256;

// On-screen size of the interactive crop viewport (square).
const CROP_VIEWPORT = 260;

const PASSWORD_RESET_URL = 'https://passwordreset.microsoftonline.com/';
const DEFAULT_CLUB_BRAND = 'Default';

const displayClubBrand = (brand?: string): string => brand?.trim() || DEFAULT_CLUB_BRAND;
const storedClubBrand = (brand?: string): string =>
  brand?.trim().toLowerCase() === DEFAULT_CLUB_BRAND.toLowerCase() ? '' : brand?.trim() ?? '';

const EMPTY_CLUB: UpsertClubInput = {
  clubType: '',
  clubBrand: '',
  clubModel: '',
  carryDistance: 0,
  totalDistance: 0,
  ballSpeed: 0,
  launchAngle: 0,
  launchDirection: 0,
  apex: 0,
  sideCarry: 0,
  clubSpeed: 0,
  smashFactor: 0,
  descentAngle: 0,
  attackAngle: 0,
  clubPath: 0,
};

// Numeric metric fields rendered in the club editor modal. The club list only
// shows Carry + Total, but the editor exposes the full set of launch-monitor
// metrics (also populated by CSV import) so a golfer can fine-tune each one.
const CLUB_METRIC_FIELDS: { key: keyof UpsertClubInput; label: string; suffix?: string; fractionDigits?: number }[] = [
  { key: 'carryDistance', label: 'Carry' },
  { key: 'totalDistance', label: 'Total' },
  { key: 'ballSpeed', label: 'Ball Speed', fractionDigits: 1 },
  { key: 'clubSpeed', label: 'Club Speed', fractionDigits: 1 },
  { key: 'smashFactor', label: 'Smash', fractionDigits: 2 },
  { key: 'launchAngle', label: 'Launch', suffix: '°', fractionDigits: 1 },
  { key: 'launchDirection', label: 'Launch Dir', suffix: '°', fractionDigits: 1 },
  { key: 'apex', label: 'Apex' },
  { key: 'descentAngle', label: 'Descent', suffix: '°', fractionDigits: 1 },
  { key: 'attackAngle', label: 'Attack', suffix: '°', fractionDigits: 1 },
  { key: 'clubPath', label: 'Club Path', suffix: '°', fractionDigits: 1 },
  { key: 'sideCarry', label: 'Side Carry', fractionDigits: 1 },
];

const PRIMARY_CLUB_METRIC_FIELDS = CLUB_METRIC_FIELDS.slice(0, 2);
const ADVANCED_CLUB_METRIC_FIELDS = CLUB_METRIC_FIELDS.slice(2);

/** One line in the club-data manager: a default club or one the golfer added. */
interface ClubRow {
  key: string;
  clubType: string;
  clubBrand: string;
  clubModel?: string;
  /** True for the 14 standard clubs (always present, not deletable). */
  isDefault: boolean;
  /** The saved average this row came from, if any. */
  average?: ClubSwingAverage;
}

const clubIdentityKey = (type?: string, brand?: string, model?: string): string =>
  `${type ?? ''}|${brand ?? ''}|${model ?? ''}`;

/**
 * Merges the 14 default clubs (always shown, not deletable) with any additional
 * saved clubs the golfer imported or added (deletable). Mirrors the iOS
 * `ClubDataView.buildRows()`.
 */
const buildClubRows = (averages: ClubSwingAverage[]): ClubRow[] => {
  const rows: ClubRow[] = [];
  const consumed = new Set<string>();

  for (const type of DEFAULT_BAG) {
    const match = averages.find((a) => a.clubType === type && !(a.clubBrand ?? '').trim());
    if (match) consumed.add(clubIdentityKey(match.clubType, match.clubBrand, match.clubModel));
    rows.push({
      key: `default-${type}`,
      clubType: type,
      clubBrand: '',
      clubModel: match?.clubModel,
      isDefault: true,
      average: match,
    });
  }

  for (const a of averages) {
    if (consumed.has(clubIdentityKey(a.clubType, a.clubBrand, a.clubModel))) continue;
    rows.push({
      key: `added-${clubIdentityKey(a.clubType, a.clubBrand, a.clubModel)}`,
      clubType: a.clubType,
      clubBrand: a.clubBrand ?? '',
      clubModel: a.clubModel,
      isDefault: false,
      average: a,
    });
  }

  return rows;
};

const ProfilePage: React.FC = () => {
  const { profile, saveProfile, loadAvatar, refreshProfile } = useProfile();
  const { user, canResetPassword } = useAuth();
  const [name, setName] = useState(profile.name);
  const [state, setState] = useState(profile.state);
  const [caddieVoicePreference, setCaddieVoicePreference] = useState<CaddieVoicePreference>(profile.caddieVoicePreference);
  const [caddieTalkativeness, setCaddieTalkativeness] = useState<CaddieTalkativeness>(profile.caddieTalkativeness);
  const [caddieVoiceStyle, setCaddieVoiceStyle] = useState<CaddieVoiceStyle>(profile.caddieVoiceStyle);
  const [caddieRecommendationDepth, setCaddieRecommendationDepth] = useState<CaddieRecommendationDepth>(profile.caddieRecommendationDepth);
  const [caddiePrimaryFocus, setCaddiePrimaryFocus] = useState<CaddiePrimaryFocus>(profile.caddiePrimaryFocus);
  const [measurementSystem, setMeasurementSystem] = useState<MeasurementSystem>(profile.measurementSystem);
  const [avatar, setAvatar] = useState(profile.avatarDataUrl || '');
  const [avatarChanged, setAvatarChanged] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);

  // Interactive avatar cropper state.
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const cropImgRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; ox: number; oy: number } | null>(null);

  const swingFileRef = useRef<HTMLInputElement>(null);
  const [averages, setAverages] = useState<ClubSwingAverage[]>([]);
  const [importing, setImporting] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(true);

  // Promo-code redemption (adds Caddie Credits to the signed-in golfer).
  const [promoCode, setPromoCode] = useState('');
  const [redeemingPromo, setRedeemingPromo] = useState(false);

  // Club editor modal state.
  const [clubDialogOpen, setClubDialogOpen] = useState(false);
  const [clubForm, setClubForm] = useState<UpsertClubInput>(EMPTY_CLUB);
  const [isEditingClub, setIsEditingClub] = useState(false);
  const [savingClub, setSavingClub] = useState(false);
  // Tile-grid club-type picker (opened from the Add Club modal).
  const [clubTypePickerOpen, setClubTypePickerOpen] = useState(false);

  const loadAverages = async () => {
    try {
      setAverages(await swingApi.getAverages(profile.userId));
    } catch {
      // non-fatal on the profile page
    }
  };

  useEffect(() => {
    loadAverages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.userId]);

  useEffect(() => {
    void loadAvatar();
  }, [loadAvatar]);

  // Reflect profile changes coming from the server hydration / a save.
  useEffect(() => {
    setName(profile.name);
    setState(profile.state);
    setCaddieVoicePreference(profile.caddieVoicePreference);
    setCaddieTalkativeness(profile.caddieTalkativeness);
    setCaddieVoiceStyle(profile.caddieVoiceStyle);
    setCaddieRecommendationDepth(profile.caddieRecommendationDepth);
    setCaddiePrimaryFocus(profile.caddiePrimaryFocus);
    setMeasurementSystem(profile.measurementSystem);
    setAvatar(profile.avatarDataUrl || '');
    setAvatarChanged(false);
  }, [
    profile.name,
    profile.state,
    profile.caddieVoicePreference,
    profile.caddieTalkativeness,
    profile.caddieVoiceStyle,
    profile.caddieRecommendationDepth,
    profile.caddiePrimaryFocus,
    profile.measurementSystem,
    profile.avatarUrl,
    profile.avatarDataUrl,
  ]);

  const handleRedeemPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;

    setRedeemingPromo(true);
    try {
      const result = await promotionApi.redeem(code, profile.userId, user?.email);
      if (result.success) {
        await refreshProfile();
        setPromoCode('');
        toast.current?.show({
          severity: 'success',
          summary: 'Promo code redeemed',
          detail: result.unlimited
            ? 'You now have unlimited Caddie Credits.'
            : `${result.creditsAdded.toLocaleString()} Caddie Credits added — ${result.creditsRemaining.toLocaleString()} available now.`,
          life: 4000,
        });
      } else {
        toast.current?.show({
          severity: 'error',
          summary: "Code can't be used",
          detail: result.error ?? 'That promo code is no longer valid.',
          life: 4000,
        });
      }
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Redeem failed',
        detail: 'Could not redeem that promo code. Please try again.',
        life: 4000,
      });
    } finally {
      setRedeemingPromo(false);
    }
  };

  const handleSwingFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setImporting(true);
    try {
      const result = await swingApi.import(file, profile.userId, replaceExisting);
      await loadAverages();
      setClubDialogOpen(false);
      toast.current?.show({
        severity: 'success',
        summary: 'Swing data imported',
        detail: `${result.imported} shot${result.imported === 1 ? '' : 's'} from ${result.totalRows} row${result.totalRows === 1 ? '' : 's'}.`,
        life: 3500,
      });
      if (result.warnings.length > 0) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Some columns were not recognized',
          detail: result.warnings.join(' '),
          life: 8000,
          sticky: true,
        });
      }
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Import failed', detail: 'Could not import that file. Use a CSV or Excel export.', life: 4000 });
    } finally {
      setImporting(false);
    }
  };

  const handleClearSwings = async () => {
    try {
      await swingApi.clear(profile.userId);
      setAverages([]);
      toast.current?.show({ severity: 'info', summary: 'Swing data cleared', life: 3000 });
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Failed', detail: 'Could not clear swing data.', life: 4000 });
    }
  };

  const openAddClub = () => {
    setClubForm(EMPTY_CLUB);
    setIsEditingClub(false);
    setClubDialogOpen(true);
  };

  const openEditClub = (avg: ClubSwingAverage) => {
    setClubForm({
      clubType: avg.clubType,
      clubBrand: displayClubBrand(avg.clubBrand),
      clubModel: avg.clubModel ?? '',
      carryDistance: toDisplayDistance(avg.avgCarryDistance, measurementSystem),
      totalDistance: toDisplayDistance(avg.avgTotalDistance, measurementSystem),
      ballSpeed: toDisplaySpeed(avg.avgBallSpeed, measurementSystem),
      launchAngle: avg.avgLaunchAngle,
      launchDirection: avg.avgLaunchDirection,
      apex: toDisplayHeight(avg.avgApex, measurementSystem),
      sideCarry: toDisplayDistance(avg.avgSideCarry, measurementSystem),
      clubSpeed: toDisplaySpeed(avg.avgClubSpeed, measurementSystem),
      smashFactor: avg.avgSmashFactor,
      descentAngle: avg.avgDescentAngle,
      attackAngle: avg.avgAttackAngle,
      clubPath: avg.avgClubPath,
    });
    setIsEditingClub(true);
    setClubDialogOpen(true);
  };

  // Edit a manager row: reuse the saved average when present, otherwise open the
  // editor prefilled with the (locked) default club type so the golfer can enter
  // its yardages for the first time.
  const openEditRow = (row: ClubRow) => {
    if (row.average) {
      openEditClub(row.average);
      return;
    }
    setClubForm({ ...EMPTY_CLUB, clubType: row.clubType, clubBrand: displayClubBrand(row.clubBrand) });
    setIsEditingClub(true);
    setClubDialogOpen(true);
  };

  const handleSaveClub = async () => {
    const clubType = clubForm.clubType.trim();
    if (!clubType) {
      toast.current?.show({ severity: 'warn', summary: 'Club type required', detail: 'Enter a club name (e.g. 7 Iron).', life: 3000 });
      return;
    }

    // Adding a new club (not editing): block duplicates. A club is identified by
    // its type + brand, where an empty brand means the default club of that type.
    if (!isEditingClub) {
      const brand = storedClubBrand(clubForm.clubBrand);
      const alreadyInBag = averages.some(
        (a) =>
          a.clubType.trim().toLowerCase() === clubType.toLowerCase() &&
          (a.clubBrand ?? '').trim().toLowerCase() === brand.toLowerCase(),
      );
      if (alreadyInBag) {
        toast.current?.show({
          severity: 'error',
          summary: 'Club already in bag',
          detail: brand
            ? `${clubType} (${brand}) is already in your bag.`
            : `${clubType} is already in your bag.`,
          life: 4000,
        });
        return;
      }
    }

    setSavingClub(true);
    try {
      await swingApi.upsertClub({
        ...clubForm,
        clubType,
        clubBrand: storedClubBrand(clubForm.clubBrand),
        carryDistance: toCanonicalDistanceYards(clubForm.carryDistance, measurementSystem),
        totalDistance: toCanonicalDistanceYards(clubForm.totalDistance, measurementSystem),
        ballSpeed: toCanonicalSpeedMph(clubForm.ballSpeed, measurementSystem),
        apex: toCanonicalHeightFeet(clubForm.apex, measurementSystem),
        sideCarry: toCanonicalDistanceYards(clubForm.sideCarry, measurementSystem),
        clubSpeed: toCanonicalSpeedMph(clubForm.clubSpeed, measurementSystem),
      }, profile.userId);
      await loadAverages();
      setClubDialogOpen(false);
      toast.current?.show({
        severity: 'success',
        summary: isEditingClub ? 'Club updated' : 'Club added',
        detail: `${clubForm.clubType.trim()} metrics saved.`,
        life: 3000,
      });
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Save failed', detail: 'Could not save club metrics.', life: 4000 });
    } finally {
      setSavingClub(false);
    }
  };

  const handleDeleteClub = async (avg: ClubSwingAverage) => {
    try {
      await swingApi.deleteClub(profile.userId, avg.clubType, avg.clubBrand, avg.clubModel);
      await loadAverages();
      toast.current?.show({ severity: 'info', summary: 'Club removed', detail: `${avg.clubType} deleted.`, life: 3000 });
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Failed', detail: 'Could not delete that club.', life: 4000 });
    }
  };

  const setClubField = (key: keyof UpsertClubInput, value: number | string) =>
    setClubForm((prev) => ({ ...prev, [key]: value }));

  const renderClubMetricField = (field: typeof CLUB_METRIC_FIELDS[number]) => (
    <div className="club-form-field" key={field.key}>
      <label htmlFor={`club-${field.key}`}>
        {field.label}
        {(() => {
          const unit = field.suffix ?? (
            field.key === 'carryDistance' || field.key === 'totalDistance' || field.key === 'sideCarry'
              ? distanceUnitLabel(measurementSystem)
              : field.key === 'ballSpeed' || field.key === 'clubSpeed'
                ? speedUnitLabel(measurementSystem)
                : field.key === 'apex'
                  ? heightUnitLabel(measurementSystem)
                  : ''
          );
          return unit ? ` (${unit})` : '';
        })()}
      </label>
      <InputNumber
        inputId={`club-${field.key}`}
        value={clubForm[field.key] as number}
        onValueChange={(e) => setClubField(field.key, e.value ?? 0)}
        minFractionDigits={0}
        maxFractionDigits={field.fractionDigits ?? 0}
        useGrouping={false}
      />
    </div>
  );

  // Merged list of the 14 default clubs plus any added clubs, rebuilt when averages change.
  const clubRows = useMemo(() => buildClubRows(averages), [averages]);

  // Only show clubs that actually have a carry or total distance on record.
  const visibleClubRows = useMemo(
    () =>
      clubRows.filter((row) => {
        const a = row.average;
        return !!a && ((a.avgCarryDistance ?? 0) > 0 || (a.avgTotalDistance ?? 0) > 0);
      }),
    [clubRows],
  );

  // Smallest scale that still covers the square viewport, then the user's zoom on top.
  const baseScale = natural.w && natural.h ? CROP_VIEWPORT / Math.min(natural.w, natural.h) : 1;
  const effScale = baseScale * zoom;
  const dispW = natural.w * effScale;
  const dispH = natural.h * effScale;

  // Zoom (relative to the cover scale) at which the entire photo fits inside the frame,
  // exposing its far edges. Used as the slider minimum and by Recenter.
  const fitZoom = natural.w && natural.h ? Math.min(natural.w, natural.h) / Math.max(natural.w, natural.h) : 1;

  // Keep the image inside the frame: cover when a dimension is larger than the viewport,
  // otherwise allow it to sit anywhere fully within the frame (so far ends are reachable).
  const clampAxis = (val: number, disp: number) =>
    disp >= CROP_VIEWPORT
      ? Math.min(0, Math.max(CROP_VIEWPORT - disp, val))
      : Math.max(0, Math.min(CROP_VIEWPORT - disp, val));

  const clampOffset = (x: number, y: number, dw: number, dh: number) => ({
    x: clampAxis(x, dw),
    y: clampAxis(y, dh),
  });

  const closeCropper = () => {
    setCropOpen(false);
    setCropSrc(null);
  };

  const onCropImgLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const base = CROP_VIEWPORT / Math.min(w, h); // zoom = 1
    setNatural({ w, h });
    setOffset({ x: (CROP_VIEWPORT - w * base) / 2, y: (CROP_VIEWPORT - h * base) / 2 });
  };

  // Zoom about the viewport center so the framed subject stays put.
  const applyZoom = (z1: number) => {
    const base = natural.w && natural.h ? CROP_VIEWPORT / Math.min(natural.w, natural.h) : 1;
    const eff0 = base * zoom;
    const eff1 = base * z1;
    const cx = (CROP_VIEWPORT / 2 - offset.x) / eff0;
    const cy = (CROP_VIEWPORT / 2 - offset.y) / eff0;
    const nx = CROP_VIEWPORT / 2 - cx * eff1;
    const ny = CROP_VIEWPORT / 2 - cy * eff1;
    setOffset(clampOffset(nx, ny, natural.w * eff1, natural.h * eff1));
    setZoom(z1);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, ox: offset.x, oy: offset.y };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset(clampOffset(dragRef.current.ox + dx, dragRef.current.oy + dy, dispW, dispH));
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  // Fit the whole photo inside the frame, centered, so all edges are usable.
  const recenter = () => {
    const base = natural.w && natural.h ? CROP_VIEWPORT / Math.min(natural.w, natural.h) : 1;
    const eff = base * fitZoom;
    setZoom(fitZoom);
    setOffset({ x: (CROP_VIEWPORT - natural.w * eff) / 2, y: (CROP_VIEWPORT - natural.h * eff) / 2 });
  };

  // Render the framed square region to the avatar canvas at output resolution.
  const applyCrop = () => {
    const img = cropImgRef.current;
    if (!img || !natural.w) return;
    const base = CROP_VIEWPORT / Math.min(natural.w, natural.h);
    const eff = base * zoom;
    const sx = -offset.x / eff;
    const sy = -offset.y / eff;
    const sSize = CROP_VIEWPORT / eff;
    const canvas = document.createElement('canvas');
    canvas.width = AVATAR_SIZE;
    canvas.height = AVATAR_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Fill first so any area outside the photo (when fitted/off-edge) is white, not black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, AVATAR_SIZE, AVATAR_SIZE);
    ctx.drawImage(img, sx, sy, sSize, sSize, 0, 0, AVATAR_SIZE, AVATAR_SIZE);
    setAvatar(canvas.toDataURL('image/jpeg', 0.85));
    setAvatarChanged(true);
    closeCropper();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setZoom(1);
      setNatural({ w: 0, h: 0 });
      setOffset({ x: 0, y: 0 });
      setCropSrc(reader.result as string);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const input: SaveProfileInput = {
        name: name.trim(),
        state,
        caddieVoicePreference,
        caddieTalkativeness,
        caddieVoiceStyle,
        caddieRecommendationDepth,
        caddiePrimaryFocus,
        measurementSystem,
      };
      if (avatarChanged && avatar.startsWith('data:')) {
        // A newly-selected image — upload the bytes to the storage account.
        input.avatar = await (await fetch(avatar)).blob();
      } else if (avatarChanged && !avatar) {
        input.removeAvatar = true;
      }
      await saveProfile(input);
      toast.current?.show({
        severity: 'success',
        summary: 'Profile saved',
        detail: 'Your profile is saved to your CaddieSight account.',
        life: 3000,
      });
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Save failed',
        detail: 'Could not save your profile. Please try again.',
        life: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  // The Save Profile button stays inactive until one of the persisted fields
  // changes; then it lights up as the primary action.
  const isDirty =
    name !== profile.name ||
    state !== profile.state ||
    caddieVoicePreference !== profile.caddieVoicePreference ||
    caddieTalkativeness !== profile.caddieTalkativeness ||
    caddieVoiceStyle !== profile.caddieVoiceStyle ||
    caddieRecommendationDepth !== profile.caddieRecommendationDepth ||
    caddiePrimaryFocus !== profile.caddiePrimaryFocus ||
    measurementSystem !== profile.measurementSystem ||
    avatarChanged;

  return (
    <div className="profile-page">
      <Toast ref={toast} />

      <div className="profile-column">
        <section className="card-surface profile-card">
        <div className="profile-avatar-row">
          <div className="profile-avatar">
            {avatar ? (
              <img src={avatar} alt="Avatar" />
            ) : (
              <i className="pi pi-user" aria-hidden="true" />
            )}
          </div>
          <div className="avatar-actions">
            <Button
              label={avatar ? 'Change Photo' : 'Upload Photo'}
              icon="pi pi-image"
              severity="secondary"
              outlined
              onClick={() => fileInputRef.current?.click()}
            />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
            aria-label="Upload avatar image"
          />
        </div>

        <div className="profile-field">
          <label htmlFor="profile-name">Name</label>
          <InputText
            id="profile-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
          />
        </div>

        <div className="profile-field">
          <label htmlFor="profile-state">Home State</label>
          <Dropdown
            inputId="profile-state"
            value={state}
            onChange={(e) => setState(e.value ?? '')}
            options={US_STATES}
            optionLabel="name"
            optionValue="code"
            placeholder="Select your state"
            filter
          />
        </div>

        <div className="profile-field">
          <label htmlFor="profile-measurement">Measurement Units</label>
          <fieldset className="adjustment-radio-group" id="profile-measurement" aria-label="Measurement Units">
            <div className="adjustment-options">
              {([
                { label: 'Imperial', value: 'imperial', detail: 'Yards, mph, feet.' },
                { label: 'Metric', value: 'metric', detail: 'Meters, km/h, meters.' },
              ] as { label: string; value: MeasurementSystem; detail: string }[]).map((option) => {
                const inputId = `measurement-${option.value}`;
                return (
                  <label key={option.value} className="adjustment-option" htmlFor={inputId}>
                    <RadioButton
                      inputId={inputId}
                      name="measurement-system"
                      value={option.value}
                      checked={measurementSystem === option.value}
                      onChange={() => setMeasurementSystem(option.value)}
                    />
                    <span>
                      <strong>{option.label}</strong>
                      <small>{option.detail}</small>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        </div>

        <div className="profile-actions">
          <Button
            label="Save Profile"
            icon="pi pi-check"
            onClick={handleSave}
            loading={saving}
            disabled={!isDirty}
            severity={isDirty ? undefined : 'secondary'}
          />
          {canResetPassword && (
            <Button
              label="Change Password"
              icon="pi pi-key"
              severity="secondary"
              outlined
              className="profile-actions__secondary"
              onClick={() => window.open(PASSWORD_RESET_URL, '_blank', 'noopener,noreferrer')}
            />
          )}
        </div>
        </section>

        <CaddieAdjustments
          voicePreference={caddieVoicePreference}
          onVoicePreferenceChange={setCaddieVoicePreference}
          talkativeness={caddieTalkativeness}
          onTalkativenessChange={setCaddieTalkativeness}
          voiceStyle={caddieVoiceStyle}
          onVoiceStyleChange={setCaddieVoiceStyle}
          recommendationDepth={caddieRecommendationDepth}
          onRecommendationDepthChange={setCaddieRecommendationDepth}
          primaryFocus={caddiePrimaryFocus}
          onPrimaryFocusChange={setCaddiePrimaryFocus}
        />
      </div>

      <div className="profile-column">
        <section className="card-surface membership-promo">
          <ProfileMembership />

          <div className="promo-redeem">
            <div className="promo-redeem__head">
              <i className="pi pi-ticket promo-redeem__icon" aria-hidden="true" />
              <div>
                <h2>Redeem a promo code</h2>
                <p className="profile-hint">Enter a promo code to add Caddie Credits to your account.</p>
              </div>
            </div>
            <div className="promo-redeem__form">
              <InputText
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleRedeemPromo(); }}
                placeholder="e.g. CADDIE-AB12CD"
                autoComplete="off"
                autoCapitalize="characters"
                aria-label="Promo code"
              />
              <Button
                label="Redeem"
                icon="pi pi-unlock"
                onClick={handleRedeemPromo}
                loading={redeemingPromo}
                disabled={!promoCode.trim()}
              />
            </div>
          </div>
        </section>

        <CourseSearchPanel />
      </div>

      <section className="card-surface swing-section">
        <div className="swing-header">
          <h2>🏌️ Club Data</h2>
          <div className="swing-header-actions">
            <Button label="Add Club" icon="pi pi-plus" severity="secondary" outlined onClick={openAddClub} />
            {averages.length > 0 && (
              <Button label="Clear" icon="pi pi-trash" severity="secondary" outlined onClick={handleClearSwings} />
            )}
          </div>
        </div>
        <p className="profile-hint">
          Import a launch-monitor CSV export, or add clubs manually. Each club is averaged to inform shot recommendations.
        </p>

        <div className="table-scroll">
          {visibleClubRows.length === 0 ? (
            <p className="swing-empty">Add a club to your bag to see its carry and total distances here.</p>
          ) : (
            <table className="swing-table">
              <thead>
                <tr>
                  <th>Club</th>
                  <th>Brand</th>
                  <th>Carry</th>
                  <th>Total</th>
                  <th aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {visibleClubRows.map((row) => {
                  const a = row.average;
                  return (
                    <tr key={row.key}>
                      <td>{[row.clubType, row.clubModel].filter(Boolean).join(' · ')}</td>
                      <td>{displayClubBrand(row.clubBrand)}</td>
                      <td>{a ? `${toDisplayDistance(a.avgCarryDistance, measurementSystem).toFixed(0)} ${distanceUnitLabel(measurementSystem)}` : '—'}</td>
                      <td>{a ? `${toDisplayDistance(a.avgTotalDistance, measurementSystem).toFixed(0)} ${distanceUnitLabel(measurementSystem)}` : '—'}</td>
                      <td className="swing-row-actions">
                        <Button
                          icon="pi pi-pencil"
                          rounded
                          text
                          severity="secondary"
                          aria-label={`Edit ${row.clubType}`}
                          onClick={() => openEditRow(row)}
                        />
                        {!row.isDefault && a && (
                          <Button
                            icon="pi pi-trash"
                            rounded
                            text
                            severity="danger"
                            aria-label={`Delete ${row.clubType}`}
                            onClick={() => handleDeleteClub(a)}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <Dialog
        header={isEditingClub ? 'Edit Club Metrics' : 'Add Club'}
        visible={clubDialogOpen}
        onHide={() => setClubDialogOpen(false)}
        className="club-dialog"
        dismissableMask
        draggable={false}
        style={{ width: '32rem', maxWidth: '95vw' }}
      >
        <div className="club-form">
          {!isEditingClub && (
            <div className="club-import">
              <div className="club-import-row">
                <Button
                  label="Import file"
                  icon="pi pi-upload"
                  severity="secondary"
                  outlined
                  loading={importing}
                  onClick={() => swingFileRef.current?.click()}
                />
                <div className="replace-toggle">
                  <Checkbox inputId="replace" checked={replaceExisting} onChange={(e) => setReplaceExisting(!!e.checked)} />
                  <label htmlFor="replace">Replace existing data</label>
                </div>
              </div>
              <small className="profile-hint">Import a launch-monitor CSV or Excel export, or enter a club manually below.</small>
              <input
                ref={swingFileRef}
                type="file"
                accept=".csv,text/csv,.xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                style={{ display: 'none' }}
                onChange={handleSwingFile}
                aria-label="Import swing data file"
              />
              <div className="club-import-divider" />
            </div>
          )}

          <div className="club-form-field">
            <label htmlFor="club-type">Club *</label>
            <button
              id="club-type"
              type="button"
              className={`club-type-select${clubForm.clubType ? '' : ' club-type-select--empty'}`}
              onClick={() => setClubTypePickerOpen(true)}
              disabled={isEditingClub}
            >
              <span>{clubForm.clubType || 'Select a club'}</span>
              <i className="pi pi-chevron-right" aria-hidden="true" />
            </button>
          </div>

          <div className="club-form-field">
            <label htmlFor="club-brand">Brand</label>
            <InputText
              id="club-brand"
              value={clubForm.clubBrand ?? ''}
              onChange={(e) => setClubField('clubBrand', e.target.value)}
              placeholder="Optional — distinguishes multiples of a club"
              disabled={isEditingClub}
            />
          </div>

          <div className="club-form-grid">
            {PRIMARY_CLUB_METRIC_FIELDS.map(renderClubMetricField)}
          </div>

          <details className="club-advanced-data">
            <summary>Advanced data</summary>
            <div className="club-form-grid">
              {ADVANCED_CLUB_METRIC_FIELDS.map(renderClubMetricField)}
            </div>
          </details>
        </div>

        <div className="club-form-actions">
          <Button label="Cancel" severity="secondary" text onClick={() => setClubDialogOpen(false)} />
          <Button
            label={isEditingClub ? 'Save Changes' : 'Add Club'}
            icon="pi pi-check"
            loading={savingClub}
            onClick={handleSaveClub}
          />
        </div>
      </Dialog>

      <Dialog
        header="Select Club"
        visible={clubTypePickerOpen}
        onHide={() => setClubTypePickerOpen(false)}
        className="club-type-picker-dialog"
        dismissableMask
        draggable={false}
        style={{ width: '32rem', maxWidth: '95vw' }}
      >
        <div className="club-tile-grid">
          {GOLF_CLUBS.map((club) => (
            <button
              key={club}
              type="button"
              className={`club-tile${clubForm.clubType === club ? ' club-tile--selected' : ''}`}
              onClick={() => {
                setClubForm((prev) => ({
                  ...prev,
                  clubType: club,
                  clubBrand: displayClubBrand(prev.clubBrand),
                }));
                setClubTypePickerOpen(false);
              }}
            >
              {club}
            </button>
          ))}
        </div>
      </Dialog>

      <Dialog
        header="Adjust Photo"
        visible={cropOpen}
        onHide={closeCropper}
        dismissableMask
        draggable={false}
        className="avatar-crop-dialog"
        style={{ width: '360px', maxWidth: '95vw' }}
      >
        <div className="avatar-cropper">
          <p className="avatar-cropper__hint">
            Drag to reposition · slide to zoom · Recenter to fit the whole photo
          </p>
          <div
            className="avatar-cropper__viewport"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
          >
            {cropSrc && (
              <img
                ref={cropImgRef}
                src={cropSrc}
                alt="Adjust avatar"
                draggable={false}
                onLoad={onCropImgLoad}
                style={{
                  width: dispW,
                  height: dispH,
                  transform: `translate(${offset.x}px, ${offset.y}px)`,
                  transformOrigin: 'top left',
                }}
              />
            )}
            {/* Circular guide showing the visible avatar area. */}
            <div className="avatar-cropper__ring" />
          </div>
          <div className="avatar-cropper__zoom">
            <i className="pi pi-image" aria-hidden="true" />
            <Slider
              value={zoom}
              min={fitZoom}
              max={4}
              step={0.01}
              onChange={(e) => applyZoom(e.value as number)}
              aria-label="Zoom"
            />
            <i className="pi pi-search-plus" aria-hidden="true" />
          </div>
        </div>
        <div className="avatar-cropper-actions">
          <Button label="Recenter" icon="pi pi-expand" text severity="secondary" onClick={recenter} />
          <div className="avatar-cropper-actions__right">
            <Button label="Cancel" severity="secondary" text onClick={closeCropper} />
            <Button label="Use Photo" icon="pi pi-check" onClick={applyCrop} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
